import type { EntryMethod, FirstField } from '@appcharge/frontend-events';
import { frontendEventsService } from '@/services/frontendEventsService';
import { getEventDataForSdkEvents } from '@/utils/eventDataBuilder';
import { mapCheckoutUiInteractionsEvent } from '@/utils/frontendEventsMapper';
import { resolveCheckoutPresentation } from '@/utils/mixpanelProperties';
import type { ICheckoutContext } from '@/types/context';
import type { AppchargePaymentElementFieldId } from '@/types/appchargePaymentElement';

/** Maps the Appcharge Payment Element's field id to the DTO's `first_field` value. */
const FIRST_FIELD_BY_FIELD_ID: Record<AppchargePaymentElementFieldId, FirstField> = {
  number: 'card_number',
  expiry: 'expiration_date',
  cvv: 'cvv',
};

let cardEntryStartedSent = false;
let formEntryCompletedSent = false;

/**
 * What `card_entry_started` reported, replayed on `form_entry_completed` so
 * both events describe the same entry. With no preceding card entry (e.g. a
 * saved card) `entry_method` stays `'unknown'` — what `card_entry_started`
 * would itself have reported — and `first_field` is left off entirely, since
 * no field was ever edited.
 */
let reportedEntryMethod: EntryMethod = 'unknown';
let reportedFirstField: FirstField | undefined;

const buildCardEntryStartedEvent = (
  context: ICheckoutContext | undefined,
  entryMethod: EntryMethod,
  firstField: FirstField
) => {
  const eventData = getEventDataForSdkEvents(context ?? {});
  return mapCheckoutUiInteractionsEvent(
    'card_entry_started',
    {
      entry_method: entryMethod,
      first_field: firstField,
      is_saved_card_offered: (context?.card?.savedCards?.length ?? 0) > 0,
    },
    eventData,
    {
      checkoutPresentation: resolveCheckoutPresentation(context),
      templateType: context?.template?.type,
    }
  );
};

const buildFormEntryCompletedEvent = (context: ICheckoutContext | undefined) => {
  const eventData = getEventDataForSdkEvents(context ?? {});
  return mapCheckoutUiInteractionsEvent(
    'form_entry_completed',
    {
      entry_method: reportedEntryMethod,
      ...(reportedFirstField ? { first_field: reportedFirstField } : {}),
      is_saved_card_offered: (context?.card?.savedCards?.length ?? 0) > 0,
    },
    eventData,
    {
      checkoutPresentation: resolveCheckoutPresentation(context),
      templateType: context?.template?.type,
    }
  );
};

/**
 * BQ counterpart of `trackCardEntryStarted` (Mixpanel) — first focus or
 * change on any Appcharge secured card field, once per checkout session.
 */
export const sendCardEntryStarted = (
  context: ICheckoutContext | undefined,
  firstField: AppchargePaymentElementFieldId,
  entryMethod: EntryMethod
): void => {
  if (cardEntryStartedSent) return;
  cardEntryStartedSent = true;
  reportedEntryMethod = entryMethod;
  reportedFirstField = FIRST_FIELD_BY_FIELD_ID[firstField];
  frontendEventsService.trackDirect(buildCardEntryStartedEvent(context, entryMethod, reportedFirstField));
};

/**
 * BQ counterpart of `trackFormEntryCompleted` (Mixpanel) — the Appcharge
 * card form first reached a payable (Pay-enabled) state, once per checkout
 * session. Reports the same `entry_method` and `first_field` as
 * `card_entry_started`, so the two events can be joined on them.
 */
export const sendFormEntryCompleted = (context: ICheckoutContext | undefined): void => {
  if (formEntryCompletedSent) return;
  formEntryCompletedSent = true;
  frontendEventsService.trackDirect(buildFormEntryCompletedEvent(context));
};

/** Test-only escape hatch — DO NOT call from production code. */
export const __resetCheckoutUiInteractionsTrackingForTests = (): void => {
  cardEntryStartedSent = false;
  formEntryCompletedSent = false;
  reportedEntryMethod = 'unknown';
  reportedFirstField = undefined;
};
